import React, { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  formatPackagePrice,
  getPackageHighlights,
  getPackageShortDescription,
} from '../utils/packagePresentation';

export default function PackageGrid() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch('/api/packages');
        if (!response.ok) {
          throw new Error('Failed to fetch packages');
        }

        const data = await response.json();
        setPackages(data.packages || []);
      } catch (err) {
        console.error('Failed to load packages:', err);
        toast.error(
          t('pricing.loadError', {
            defaultValue: 'Packages could not be loaded',
          }),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, [t]);

  const handleBuyClick = (pkg) => {
    if (pkg.price === 0) {
      window.location.href = '/app/?demo=demo';
      return;
    }

    window.location.href = `/checkout?package=${pkg.id}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const sortedPackages = [...packages].sort((a, b) => a.price - b.price);

  return (
    <section id="pricing" className="bg-gradient-to-b from-slate-900 to-slate-800 px-4 py-16">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12 text-center"
        >
          <h2 className="mb-4 text-4xl font-bold text-white md:text-5xl">
            {t('pricing.title', { defaultValue: 'Packages at a Glance' })}
          </h2>
          <p className="mx-auto max-w-2xl text-xl text-slate-300">
            {t('pricing.subtitle', {
              defaultValue: 'Whether Postcard, family memories or JGA moments - we have the right package.',
            })}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {sortedPackages.map((pkg, idx) => {
            const packagePrice = formatPackagePrice(
              pkg.price,
              i18n.language,
              t('comparison.value.free', { defaultValue: 'Free' }),
            );
            const packageHighlights = getPackageHighlights(pkg, t);

            return (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className={`relative overflow-hidden rounded-2xl border backdrop-blur-xl transition-all duration-300 hover:scale-105 ${
                  pkg.recommended
                    ? 'border-purple-500/50 bg-gradient-to-br from-purple-900/40 to-slate-900/40 shadow-2xl shadow-purple-500/20 ring-2 ring-purple-500/30'
                    : 'border-slate-700/50 bg-slate-800/30'
                }`}
              >
                {pkg.recommended && (
                  <div className="absolute right-4 top-4 rounded-full bg-purple-500 px-3 py-1 text-sm font-semibold text-white">
                    {t('pricing.bestSeller', { defaultValue: 'BESTSELLER' })}
                  </div>
                )}

                <div className="p-8">
                  <div className="mb-2 flex items-center gap-3">
                    <span className="text-4xl">{pkg.icon}</span>
                    <h3 className="text-2xl font-bold text-white">{pkg.name}</h3>
                  </div>

                  <p className="mb-4 h-10 text-sm text-slate-300">
                    {getPackageShortDescription(pkg, t)}
                  </p>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-white">{packagePrice}</span>
                      {pkg.price > 0 && (
                        <span className="text-sm text-slate-400">
                          {t('pricing.oneTime', { defaultValue: 'one-time' })}
                        </span>
                      )}
                    </div>
                  </div>

                  <ul className="mb-8 space-y-3">
                    {packageHighlights.map((highlight, index) => (
                      <li key={`${pkg.id}-highlight-${index}`} className="flex items-start gap-3 text-sm">
                        <Check size={18} className="mt-0.5 flex-shrink-0 text-green-400" />
                        <span className="text-slate-200">{highlight}</span>
                      </li>
                    ))}
                  </ul>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleBuyClick(pkg)}
                    className={`w-full rounded-lg px-4 py-3 font-semibold transition-all duration-200 ${
                      pkg.recommended
                        ? 'bg-gradient-to-r from-purple-600 to-purple-500 text-white shadow-lg shadow-purple-500/30 hover:from-purple-500 hover:to-purple-400'
                        : 'bg-purple-600 text-white hover:bg-purple-500'
                    }`}
                  >
                    {pkg.price > 0
                      ? t('pricing.buyNow', {
                          price: packagePrice,
                          defaultValue: 'Buy now - {{price}}',
                        })
                      : t('pricing.demoCta', { defaultValue: 'Try for free' })}
                  </motion.button>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 text-center"
        >
          <a
            href="#feature-comparison"
            className="inline-flex items-center gap-2 rounded-lg border border-purple-500/50 bg-purple-600/20 px-6 py-3 text-purple-300 transition-all duration-200 hover:bg-purple-600/30 hover:text-purple-200"
          >
            {t('pricing.compareCta', { defaultValue: 'Detailed package comparison' })}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
